import { Transition } from '@headlessui/react';
import { XCircleIcon } from '@heroicons/react/24/outline';
import AuthStore from 'bz-stores/lib/auth-store';
import JsonServiceClient from 'bz-stores/lib/JsonServiceClient';
import history from 'bz-utilities/modules/networking/history/rr4';
import { Button } from 'click-controls';
import Switch from 'click-controls/controls/form/field/field-switch';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import CodeInput from 'react-auth-code-input';
import { Link } from 'react-router-dom';
import { TwoFactorVerify } from 'tm3-core/api.dtos';
import { TwoFactorRequestCode } from '../../../../../shared/tm3-core/api.dtos';
import { navigateToRedirectTarget } from '../../redirect-target';

function Challenge({
  onPass,
  showTitle = true,
  showTrust = true
}: {
  onPass?: (code: string) => void;
  showTitle: boolean;
  showTrust: boolean;
}) {
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [failMessage, setFailMessage] = useState('');

  const onSubmitChallenge = useCallback(
    async (e) => {
      e?.preventDefault();
      try {
        const { result } = await new JsonServiceClient().post(
          new TwoFactorVerify({ code, remember })
        );
        if (!result) {
          setHasFailed(true);
          return;
        }
        if (onPass) {
          return onPass(code);
        }
        AuthStore.onReset();
        navigateToRedirectTarget();
      } catch (err) {
        if (err.responseStatus) {
          const { message, errorCode } = err.responseStatus;
          setHasFailed(true);
          setFailMessage(message);
          if (errorCode === 'Unauthorized') {
            setTimeout(() => history.replace('login'), 3000);
          }
        }
      }
    },
    [code, remember]
  );

  const onRequestCode = useCallback(async () => {
    const { result } = await new JsonServiceClient().post(
      new TwoFactorRequestCode({ useSms: true })
    );
    return result;
  }, []);

  const onSetCode = useCallback(
    (code: string) => {
      setTimeout(() => {
        setCode(code);
        setHasFailed(false);
      }, 1000);
    },
    [code]
  );

  useEffect(() => {
    if (code.length === 6) onSubmitChallenge(null);
  }, [code]);
  const { twoFactorAllowSms } = AuthStore.user;
  return (
    <form
      id="form-login"
      role="form"
      className="grid justify-items-center"
      onSubmit={onSubmitChallenge}
    >
      {showTitle && (
        <h3 className="mb-4 text-3xl font-bold">
          Please enter your Two Factor Authentication Code
        </h3>
      )}
      <p className="mb-6 text-base">
        Open up your authenticator app and get the code
        {twoFactorAllowSms && (
          <>
            <span>, or </span>
            <a className="cursor-pointer" onClick={onRequestCode}>
              click here to be sent a code by SMS
            </a>
          </>
        )}
        .
      </p>
      <div>
        <CodeInput
          ariaLabel="Code"
          containerClassName="flex"
          inputClassName="w-10 p-1 m-1 border border-gray-300 shadow-md rounded-md text-2xl font-bold text-center focus:shadow-blue-300 focus:border-blue-300"
          allowedCharacters="numeric"
          autoFocus={true}
          onChange={onSetCode}
        />
      </div>
      <Transition
        as={Fragment}
        show={hasFailed}
        enter="transform transition duration-[400ms]"
        enterFrom="opacity-0 scale-50"
        enterTo="opacity-100 rotate-0 scale-100"
        leave="transform duration-200 transition ease-in-out"
        leaveFrom="opacity-100 rotate-0 scale-100 "
        leaveTo="opacity-0 scale-95 "
      >
        <div className="mt-2 flex  items-center justify-center">
          <XCircleIcon className="h-12 stroke-rose-500" />
          <span className="text-rose-500">{failMessage || 'Your supplied code was incorrect'}</span>
        </div>
      </Transition>
      {showTrust && (
        <>
          <div className="mt-4 flex flex-row items-center  text-base">
            <span className="mt-1">
              <Switch isSelected={remember} onChange={() => setRemember(!remember)} />
            </span>
            <div className="ml-2">Trust this device for 30 days</div>
          </div>
          <div className="ml-2 text-sm text-gray-400">
            (Do not use this feature if you share this device/computer with anyone else)
          </div>
        </>
      )}
      <input type="submit" className="btn mt-6" value="Continue" />
      <Link className="mt-2 text-sm" to="/login">
        Back to Login
      </Link>
    </form>
  );
}

export { Challenge };
